// Socket.IO client for real-time match updates
import { io, Socket } from 'socket.io-client';
import { getToken, getCurrentApiUrl } from './client';

let socket: Socket | null = null;
let connectionPromise: Promise<Socket> | null = null;

export interface MatchUpdateEvent {
  match: {
    id: string;
    name: string;
    gameName: string;
    gameId: string;
    status: string;
    majorVersion: number;
    versionChannel?: string;
    players: Record<string, unknown>;
    state?: unknown;
    lastUpdated?: string;
  };
}

type MatchUpdateCallback = (data: MatchUpdateEvent) => void;
type MatchDeletedCallback = (data: { matchId: string }) => void;
type GameUpdateCallback = (data: { game: unknown }) => void;

const matchCallbacks = new Map<string, Set<MatchUpdateCallback>>();
const matchDeletedCallbacks = new Map<string, Set<MatchDeletedCallback>>();
const gameCallbacks = new Map<string, Set<GameUpdateCallback>>();

/**
 * Get or create the socket connection
 */
export function getSocket(): Promise<Socket> {
  if (socket?.connected) {
    return Promise.resolve(socket);
  }

  if (connectionPromise) {
    return connectionPromise;
  }

  connectionPromise = new Promise((resolve, reject) => {
    const token = getToken();
    if (!token) {
      reject(new Error('Not authenticated'));
      connectionPromise = null;
      return;
    }

    socket = io(getCurrentApiUrl(), {
      auth: { token },
      transports: ['websocket', 'polling'],
    });

    socket.on('connect', () => {
      console.log('Socket connected:', socket!.id);
      resolve(socket!);
    });

    socket.on('connect_error', (error) => {
      console.error('Socket connection error:', error);
      connectionPromise = null;
      reject(error);
    });

    socket.on('disconnect', (reason) => {
      console.log('Socket disconnected:', reason);
      connectionPromise = null;
    });

    // Handle match update events
    socket.on('match:updated', (data: MatchUpdateEvent) => {
      const matchId = data.match.id;
      const callbacks = matchCallbacks.get(matchId);
      if (callbacks) {
        callbacks.forEach(cb => cb(data));
      }
    });

    // Handle match deleted events
    socket.on('match:deleted', (data: { matchId: string }) => {
      const callbacks = matchDeletedCallbacks.get(data.matchId);
      if (callbacks) {
        callbacks.forEach(cb => cb(data));
      }
    });

    // Handle game update events
    socket.on('game:updated', (data: { game: unknown }) => {
      const game = data.game as { id?: string };
      const gameId = game?.id;
      if (gameId) {
        const callbacks = gameCallbacks.get(gameId);
        if (callbacks) {
          callbacks.forEach(cb => cb(data));
        }
      }
    });
  });

  return connectionPromise;
}

/**
 * Subscribe to updates for a specific match
 */
export async function subscribeToMatch(
  matchId: string,
  onUpdate: MatchUpdateCallback,
  onDeleted?: MatchDeletedCallback
): Promise<() => void> {
  const sock = await getSocket();

  // Register callbacks
  if (!matchCallbacks.has(matchId)) {
    matchCallbacks.set(matchId, new Set());
  }
  matchCallbacks.get(matchId)!.add(onUpdate);

  if (onDeleted) {
    if (!matchDeletedCallbacks.has(matchId)) {
      matchDeletedCallbacks.set(matchId, new Set());
    }
    matchDeletedCallbacks.get(matchId)!.add(onDeleted);
  }

  // Tell server we want updates for this match
  sock.emit('match:subscribe', matchId);

  // Return unsubscribe function
  return () => {
    matchCallbacks.get(matchId)?.delete(onUpdate);
    if (matchCallbacks.get(matchId)?.size === 0) {
      matchCallbacks.delete(matchId);
      sock.emit('match:unsubscribe', matchId);
    }

    if (onDeleted) {
      matchDeletedCallbacks.get(matchId)?.delete(onDeleted);
      if (matchDeletedCallbacks.get(matchId)?.size === 0) {
        matchDeletedCallbacks.delete(matchId);
      }
    }
  };
}

/**
 * Emit an event through the shared socket with an acknowledgement callback
 */
export async function emitWithAck<T>(
  event: string,
  data: unknown,
  callback: (response: T) => void
): Promise<void> {
  const sock = await getSocket();
  sock.emit(event, data, callback);
}

/**
 * Claim or renew the AI driver lease for a client-trust match. Resolves to whether this tab should
 * let its copy of the engine answer for the AI seats, and how soon to renew.
 */
export async function claimAiDriver(matchId: string): Promise<{ driver: boolean; renewMs: number }> {
  const sock = await getSocket();
  return new Promise((resolve) => {
    sock
      .timeout(10_000)
      .emit('match:claimAiDriver', { matchId }, (err: unknown, response?: { driver: boolean; renewMs: number }) => {
        if (err || !response) {
          resolve({ driver: false, renewMs: 5_000 });
        } else {
          resolve(response);
        }
      });
  });
}

/**
 * Give the AI driver lease back so another tab can take over immediately.
 */
export async function releaseAiDriver(matchId: string): Promise<void> {
  try {
    const sock = await getSocket();
    sock.emit('match:releaseAiDriver', { matchId });
  } catch {
    // Not connected: the lease lapses on its own
  }
}

/**
 * Subscribe to updates for a specific game
 */
export async function subscribeToGame(
  gameId: string,
  onUpdate: GameUpdateCallback
): Promise<() => void> {
  const sock = await getSocket();

  // Register callback
  if (!gameCallbacks.has(gameId)) {
    gameCallbacks.set(gameId, new Set());
  }
  gameCallbacks.get(gameId)!.add(onUpdate);

  // Tell server we want updates for this game
  sock.emit('game:subscribe', gameId);

  // Return unsubscribe function
  return () => {
    gameCallbacks.get(gameId)?.delete(onUpdate);
    if (gameCallbacks.get(gameId)?.size === 0) {
      gameCallbacks.delete(gameId);
      sock.emit('game:unsubscribe', gameId);
    }
  };
}

/**
 * Disconnect the socket (call on logout or when no longer needed)
 */
export function disconnectSocket(): void {
  if (socket) {
    socket.disconnect();
    socket = null;
    connectionPromise = null;
    matchCallbacks.clear();
    matchDeletedCallbacks.clear();
    gameCallbacks.clear();
  }
}

